/**
 * Sandbox-safe replacement for window.confirm().
 *
 * The Emergent preview iframe is sandboxed without `allow-modals`, so the
 * native confirm() / alert() / prompt() calls are silently ignored — that
 * caused delete buttons across admin to do nothing.
 *
 * Usage:
 *
 *   import { useConfirm, ConfirmDialog } from '@/lib/confirm';
 *
 *   function MyComponent() {
 *     const { confirm, dialogProps } = useConfirm();
 *     return (
 *       <>
 *         <Button onClick={async () => {
 *           if (await confirm('Delete this?', 'This cannot be undone.')) {
 *             // do delete
 *           }
 *         }}>Delete</Button>
 *         <ConfirmDialog {...dialogProps} />
 *       </>
 *     );
 *   }
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function useConfirm() {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState({ title: '', description: '', confirmText: 'Confirm', cancelText: 'Cancel', destructive: false });
  const resolverRef = useRef(null);

  const confirm = useCallback((title, description, opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setConfig({
        title: title || 'Are you sure?',
        description: description || '',
        confirmText: opts.confirmText || 'Confirm',
        cancelText: opts.cancelText || 'Cancel',
        destructive: !!opts.destructive,
      });
      setOpen(true);
    });
  }, []);

  const handleResolve = (value) => {
    setOpen(false);
    if (resolverRef.current) resolverRef.current(value);
    resolverRef.current = null;
  };

  return {
    confirm,
    dialogProps: {
      open,
      config,
      onConfirm: () => handleResolve(true),
      onCancel: () => handleResolve(false),
    },
  };
}

export function ConfirmDialog({ open, config, onConfirm, onCancel }) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{config.title}</AlertDialogTitle>
          {config.description && (
            <AlertDialogDescription className="whitespace-pre-line">
              {config.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} data-testid="confirm-dialog-cancel">
            {config.cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            data-testid="confirm-dialog-confirm"
            className={config.destructive ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
          >
            {config.confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
